/**
 * The recording formats a coach can upload for transcription.
 *
 * Mirrors `backend/app/data/audio_upload_formats.json` (the backend decides
 * acceptance from the sniffed file; this list only drives the file pickers).
 * `__tests__/audioUploadFormats.test.ts` keeps the two equal and checks that
 * every audio picker uses these exports instead of an inline MIME list.
 */

export interface AudioUploadFormat {
  id: string;
  label: string;
  container: string;
  extensions: string[];
  mime_types: string[];
}

export const AUDIO_UPLOAD_FORMATS: AudioUploadFormat[] = [
  {
    id: 'mp3',
    label: 'MP3',
    container: 'audio/mpeg',
    extensions: ['.mp3'],
    mime_types: ['audio/mpeg', 'audio/mp3', 'audio/mpeg3', 'audio/x-mpeg', 'audio/x-mpeg-3', 'audio/x-mp3', 'audio/mpg'],
  },
  {
    id: 'm4a',
    label: 'M4A',
    container: 'audio/mp4',
    extensions: ['.m4a', '.m4b'],
    mime_types: ['audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/x-m4b', 'audio/mp4a-latm'],
  },
  {
    id: 'aac',
    label: 'AAC',
    container: 'audio/aac',
    extensions: ['.aac'],
    mime_types: ['audio/aac', 'audio/x-aac', 'audio/aacp', 'audio/x-hx-aac-adts'],
  },
  {
    id: 'wav',
    label: 'WAV',
    container: 'audio/wav',
    extensions: ['.wav', '.wave'],
    mime_types: ['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave', 'audio/x-pn-wav'],
  },
  {
    id: 'webm',
    label: 'WebM',
    container: 'audio/webm',
    extensions: ['.webm'],
    mime_types: ['audio/webm', 'video/webm'],
  },
  {
    id: 'ogg',
    label: 'OGG/Opus',
    container: 'audio/ogg',
    extensions: ['.ogg', '.oga', '.opus'],
    mime_types: ['audio/ogg', 'audio/opus', 'audio/x-ogg', 'audio/vorbis', 'application/ogg'],
  },
  {
    id: 'flac',
    label: 'FLAC',
    container: 'audio/flac',
    extensions: ['.flac'],
    mime_types: ['audio/flac', 'audio/x-flac'],
  },
  {
    id: 'mp4',
    label: 'MP4',
    container: 'audio/mp4',
    extensions: ['.mp4', '.m4v'],
    mime_types: ['video/mp4', 'video/x-m4v'],
  },
  {
    id: 'mov',
    label: 'MOV',
    container: 'audio/mp4',
    extensions: ['.mov'],
    mime_types: ['video/quicktime'],
  },
];

function unique(values: string[]): string[] {
  return values.filter((value, index) => values.indexOf(value) === index);
}

/** Every MIME type a picker should offer (containers and the aliases browsers report). */
export const AUDIO_UPLOAD_MIME_TYPES: string[] = unique(
  AUDIO_UPLOAD_FORMATS.flatMap((format) => [format.container, ...format.mime_types]),
);

/** Lower-case extensions with the leading dot (`.mp3`, `.m4a`, ...). */
export const AUDIO_UPLOAD_EXTENSIONS: string[] = unique(AUDIO_UPLOAD_FORMATS.flatMap((format) => format.extensions));

/** Display names for "supported formats" copy (`MP3, M4A, ...`). */
export const AUDIO_UPLOAD_LABELS: string[] = unique(AUDIO_UPLOAD_FORMATS.map((format) => format.label));

/** `accept` list for a web `<input type="file">`: MIME types plus extensions (some browsers only match one). */
export const AUDIO_UPLOAD_WEB_ACCEPT: string[] = [...AUDIO_UPLOAD_MIME_TYPES, ...AUDIO_UPLOAD_EXTENSIONS];

function extensionOf(name: string | null | undefined): string {
  const lower = (name || '').toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
}

/**
 * Whether a picked file looks like an accepted recording, by extension or by
 * MIME type (parameters such as `;codecs=opus` are ignored). The server still
 * sniffs the bytes; this only routes the file to the transcription flow.
 */
export function isAudioUploadFile(name: string | null | undefined, mimeType?: string | null): boolean {
  if (AUDIO_UPLOAD_EXTENSIONS.includes(extensionOf(name))) return true;
  const mime = (mimeType || '').split(';')[0].trim().toLowerCase();
  return mime !== '' && AUDIO_UPLOAD_MIME_TYPES.includes(mime);
}
